import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Delete, Lock } from "lucide-react";

export default function PinLock() {
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [pinSet, setPinSet] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [firstPin, setFirstPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("session_token")) {
      nav("/dashboard");
      return;
    }
    api.authStatus().then((r) => setPinSet(r.pin_set)).catch(() => setPinSet(false));
  }, [nav]);

  const submit = async (val) => {
    setBusy(true);
    try {
      if (pinSet) {
        const res = await api.verifyPin(val);
        localStorage.setItem("session_token", res.token);
        nav("/dashboard");
      } else {
        if (!confirming) {
          setFirstPin(val);
          setConfirming(true);
          setPin("");
          toast.info("Confirm your PIN");
        } else {
          if (val !== firstPin) {
            setConfirming(false);
            setFirstPin("");
            setPin("");
            toast.error("PINs don't match");
          } else {
            const res = await api.setupPin(val);
            localStorage.setItem("session_token", res.token);
            nav("/dashboard");
          }
        }
      }
    } catch (e) {
      setPin("");
      toast.error(e?.response?.data?.detail || "Incorrect PIN");
    } finally {
      setBusy(false);
    }
  };

  const press = (n) => {
    if (busy) return;
    if (pin.length >= 4) return;
    const next = pin + String(n);
    setPin(next);
    if (next.length === 4) submit(next);
  };

  const backspace = () => setPin((p) => p.slice(0, -1));

  const title = pinSet === null
    ? "Loading"
    : pinSet
      ? "Welcome back"
      : confirming
        ? "Confirm PIN"
        : "Create PIN";

  const subtitle = pinSet
    ? "Enter your PIN to continue"
    : "Set a 4-digit PIN to secure your data";

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-10">
      <div className="glass-strong w-full max-w-sm p-8" data-testid="pin-lock-screen">
        <div className="flex flex-col items-center text-center mb-7">
          <div className="w-14 h-14 rounded-2xl glass-pill flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-white" strokeWidth={2.2} />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white">{title}</h1>
          <p className="text-sm text-white/60 mt-1">{pinSet === null ? " " : subtitle}</p>
        </div>

        <div className="flex justify-center gap-3 mb-7" data-testid="pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-3.5 h-3.5 rounded-full ${
                pin.length > i ? "bg-white" : "bg-white/20"
              }`}
            />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2.5">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              onClick={() => press(n)}
              disabled={busy}
              className="glass-pill h-14 text-xl font-heading font-semibold text-white active:opacity-70"
              data-testid={`pin-key-${n}`}
            >
              {n}
            </button>
          ))}
          <div />
          <button
            onClick={() => press(0)}
            disabled={busy}
            className="glass-pill h-14 text-xl font-heading font-semibold text-white active:opacity-70"
            data-testid="pin-key-0"
          >
            0
          </button>
          <button
            onClick={backspace}
            disabled={busy}
            className="glass-pill h-14 flex items-center justify-center text-white/80 active:opacity-70"
            data-testid="pin-key-back"
          >
            <Delete className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
